<script setup lang="ts">
import { OdsDivider } from "@ods/lidl-web-vuejs";

import type { ColorScheme, Scenario } from "@reco/shared/src/constants";
import type { Recommendation } from "@reco/shared/src/types/recommendation";

import { getGridboxType } from "@reco/service/src/utils/helpers";
import RecoHeading from "@reco/shared/src/components/RecoHeading.vue";
import GridBoxFragment from "@reco/widgets/src/components/GridboxFragment.vue";
import { useHeading } from "@reco/widgets/src/composables/heading";
import { useShouldRender } from "@reco/widgets/src/composables/shouldRender";
import { useSubheading } from "@reco/widgets/src/composables/subheading";
import { usePageStore } from "@reco/widgets/src/stores/pageStore";

export type ListProps = {
  heading: string;
  subHeading: string;
  headingElement?: string;
  showTitle?: boolean;
  colorScheme: ColorScheme;
  recommendations: Recommendation[];
  creativeSlot: string;
  scenario: Scenario;
  brand?: string;
  category?: string;
  categoryName?: string;
};

const props = defineProps<ListProps>();

const finalHeading = useHeading(() => ({
  scenario: props.scenario,
  headingProp: props.heading,
  brand: props.brand,
  category: props.category,
  categoryName: props.categoryName,
}));
const finalSubHeading = useSubheading(() => ({ scenario: props.scenario, subheadingProp: props.subHeading }));
const { shouldRenderWidget, shouldRenderTitle } = useShouldRender(props, finalHeading, "List");
const { country } = usePageStore();
</script>

<template>
  <div v-if="shouldRenderWidget" class="reco-list">
    <RecoHeading
      v-if="shouldRenderTitle"
      :sub-heading="finalSubHeading"
      :heading-element="headingElement"
      :small="true"
      :color-scheme="colorScheme"
    >
      {{ finalHeading }}
    </RecoHeading>
    <div>
      <div
        v-for="(item, idx) in recommendations"
        :key="idx"
        :class="['reco-list__fragment-wrap', `reco-list__fragment-wrap--${getGridboxType(country, item.list)}`]"
      >
        <GridBoxFragment :product="item" :creative-slot="props.creativeSlot" />
        <OdsDivider v-if="idx < recommendations.length - 1" class="reco-list__divider" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.reco-list__fragment-wrap {
  height: 100%;
  display: grid;
  grid-template-rows: 1.5rem 1fr;
  position: relative;
  grid-template-rows: auto;
}

.reco-list__divider {
  margin: 0.5rem 0;
}
</style>
